import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  outboundWorkflowService,
  type OutboundStage,
  type OutboundWorkflow,
} from '@/services/outboundWorkflowService';

const SAMPLE_CSV = [
  'First Name,Last Name,Company,Position,Email,LinkedIn URL,Phone (Work),Phone (Personal),Workflow,Stage,Notes',
  'Ada,Lovelace,Analytical Engines,CTO,,https://www.linkedin.com/in/ada,+1-202-555-0101,+1-202-555-0147,LinkedIn outbound,Up Next,',
  'Grace,Hopper,Navy,Admiral,grace@navy.test,,,,LinkedIn outbound,Invited,',
  'Marie,Example,Example PE,Partner,marie@example.com,,,,LinkedIn outbound,Nurture review,Keep in touch after next role',
].join('\n');

function label(t: (k: string) => string, key: string, fallback: string): string {
  const v = t(key);
  return v === key ? fallback : v;
}

export const OutboundImportPage: React.FC = () => {
  const { t } = useLanguage();
  const [workflows, setWorkflows] = useState<OutboundWorkflow[]>([]);
  const [stagesByWorkflow, setStagesByWorkflow] = useState<Record<string, OutboundStage[]>>({});
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    created: number;
    updated: number;
    enrolled: number;
    skipped: Array<{ line: number; reason: string }>;
  } | null>(null);

  useEffect(() => {
    void (async () => {
      const wfs = await outboundWorkflowService.listWorkflows();
      setWorkflows(wfs);
      const grouped: Record<string, OutboundStage[]> = {};
      for (const wf of wfs) {
        grouped[wf.id] = await outboundWorkflowService.listStages(wf.id);
      }
      setStagesByWorkflow(grouped);
    })();
  }, []);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setCsv(await file.text());
    setResult(null);
    setError(null);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tmos-outbound-import.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!csv.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await outboundWorkflowService.importCsv(csv));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="space-y-4 max-w-3xl">
      <header>
        <h1 className="text-lg font-semibold text-[var(--color-primary-font)]">
          {label(t, 'outbound.import.title', 'Upload people + stage')}
        </h1>
        <p className="text-sm mt-1 text-[var(--color-surface-text)]">
          {label(
            t,
            'outbound.import.help',
            'Each row is a person, the workflow they belong to, and the stage they are on now. T-MOS creates or updates the contact and enrolls them on that workflow stage. Email is optional. History starts when you work Today — this file does not invent past steps.'
          )}
        </p>
      </header>

      <section className="rounded border border-[var(--color-border)] p-3 space-y-2">
        <h2 className="text-sm font-medium">{label(t, 'outbound.import.sample', 'Download sample')}</h2>
        <p className="text-xs opacity-80">
          {label(
            t,
            'outbound.import.templateHelp',
            'Download the CSV template. Set Workflow and Stage on every row. Include LinkedIn URL, Phone (Work), and Phone (Personal) when you have them.'
          )}
        </p>
        <button
          type="button"
          className="rounded px-3 py-1.5 text-sm bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)]"
          onClick={downloadSample}
        >
          {label(t, 'outbound.import.sample', 'Download sample')}
        </button>
      </section>

      <section className="rounded border border-[var(--color-border)] p-3 space-y-2">
        <h2 className="text-sm font-medium">{label(t, 'outbound.import.choose', 'Choose CSV')}</h2>
        <p className="text-xs opacity-80">
          {label(
            t,
            'outbound.import.stagesHint',
            'Workflow and Stage must match a pair below (name or code). The same stage name on two workflows is not interchangeable.'
          )}
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="text-sm rounded px-3 py-1.5 border border-[var(--color-border)] cursor-pointer">
            {label(t, 'outbound.import.choose', 'Choose CSV')}
            <input
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
          </label>
          {fileName && <span className="text-xs">{fileName}</span>}
          <button
            type="button"
            disabled={busy || !csv.trim()}
            className="rounded px-3 py-1.5 text-sm bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)] disabled:opacity-50"
            onClick={() => void submit()}
          >
            {label(t, 'outbound.import.run', 'Import')}
          </button>
        </div>
        {error && <p className="text-sm text-red-700">{error}</p>}
        {result && (
          <div className="text-sm space-y-1">
            <p>
              {label(
                t,
                'outbound.import.result',
                'Created {created}, updated {updated}, placed on a stage {enrolled}.'
              )
                .replace('{created}', String(result.created))
                .replace('{updated}', String(result.updated))
                .replace('{enrolled}', String(result.enrolled))}
            </p>
            {result.skipped.length > 0 && (
              <ul className="text-xs max-h-40 overflow-auto space-y-0.5">
                {result.skipped.map((s) => (
                  <li key={`${s.line}-${s.reason}`}>
                    Line {s.line}: {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {workflows.length > 0 && (
        <section className="rounded border border-[var(--color-border)] p-3 space-y-3">
          <h2 className="text-sm font-medium">
            {label(t, 'outbound.import.validStages', 'Valid Workflow and Stage values')}
          </h2>
          {workflows.map((wf) => (
            <div key={wf.id} className="space-y-1">
              <p className="text-xs font-medium">
                {wf.name} <span className="opacity-60">({wf.code})</span>
              </p>
              <ul className="text-xs columns-2 gap-4">
                {(stagesByWorkflow[wf.id] || []).map((s) => (
                  <li key={s.id}>
                    {s.name} <span className="opacity-60">({s.code})</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}
    </main>
  );
};
